import {
	Modal, Text, H3, Spacer, Button, WarnChip
} from '@components';
import { BUTTON_TYPES, TEXT_WEIGHT, TRaisedClaim } from '@types';
import { formatPriceToFinancial } from '@utils';
import styles from './ShipperClaimRaisedModal.module.scss';

export interface ShipperClaimRaisedModalProps {
	claims: TRaisedClaim[];
	onClose: () => void;
	/* Absent where the claims cannot be reached from the page the edit was made on. */
	onViewClaims?: () => void;
}

const signedAmount = (claim: TRaisedClaim) => {
	const amount = Number(claim.total ?? 0);

	return `${amount < 0 ? '-' : '+'} $${formatPriceToFinancial(`${Math.abs(amount)}`)}`;
};

/**
 * Shown after an edit past the shipper's invoice, where the difference was charged as an I&C rather than
 * moving the price — an outcome the admin would otherwise have to go looking for. One row per claim: the
 * freight and the modifiers are charged apart from one another, so a single edit can raise two.
 */
export const ShipperClaimRaisedModal = (props: ShipperClaimRaisedModalProps) => {
	const { claims, onClose, onViewClaims } = props;
	const isPlural = claims.length > 1;

	return (
		<Modal open={claims.length > 0} toggleOpen={onClose} closeOnEscape>
			<H3>{isPlural ? 'Shipper I&Cs raised' : 'Shipper I&C raised'}</H3>
			<Spacer size={8} />
			<Text>
				{isPlural
					? 'Your changes are saved. The shipper differences have been raised as I&Cs.'
					: 'Your changes are saved. The shipper difference has been raised as an I&C.'}
			</Text>
			<Spacer size={24} />
			<div className={styles.row}>
				<Text>Shipment</Text>
				{/* Every claim here came from one edit to one shipment, so the first one names it for all. */}
				<Text>{claims[0]?.shipment_reference}</Text>
			</div>
			<div className={styles.row}>
				<Text>Reason</Text>
				{/* Every claim from one edit carries the same reason, so the first one names it for all. */}
				<Text>{claims[0]?.reason}</Text>
			</div>
			<div className={styles.row}>
				<Text>Status</Text>
				<WarnChip text='Pending review' />
			</div>
			<Spacer size={16} />
			<hr className={styles.divider} />
			<Spacer size={16} />
			{/* The reason is the same on every row, so each names only the half of the price it charges. */}
			{claims.map((claim) => (
				<div className={styles.row} key={claim.id}>
					<Text>{claim.sub_reason}</Text>
					<Text weight={TEXT_WEIGHT.SEMI_BOLD}>{signedAmount(claim)}</Text>
				</div>
			))}
			<Spacer size={24} />
			<div className={styles.actions}>
				<Button text='Close' type={BUTTON_TYPES.TERTIARY} onClick={onClose} />
				{onViewClaims && <Button text={isPlural ? 'View I&Cs' : 'View I&C'} onClick={onViewClaims} />}
			</div>
		</Modal>
	);
}
